/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import { CircleDollarSign } from 'lucide-react';
import { useState } from 'react';

/* Import components */
import CodeBlock from '@/components/code/CodeBlock';
import {
    ElectricityIcon,
    HeatIcon,
    PhotovoltaicsIcon,
} from '@/components/icon/SupplyIcon';
import LayoutSectionHeader from '@/components/tabs/layouts/LayoutSectionHeader';
import SystemDashboardSectionLabel from '@/components/tabs/overview/system/SystemDashboardSectionLabel';
import SystemDashboardTile from '@/components/tabs/overview/system/SystemDashboardTile';

/* Import types */
import type {
    SystemDashboardSectionLabelAccent,
    SystemDashboardSectionLabelAnomalies,
} from '@/components/tabs/overview/system/SystemDashboardSectionLabel';
import type { SystemDashboardTileData } from '@/components/tabs/overview/system/SystemDashboardTile';

interface ComponentsLayoutsTabProps {
    theme: 'light' | 'dark';
}

const SYSTEM_DASHBOARD_SECTION_LABEL_EXAMPLES = [
    {
        accent: 'photovoltaics',
        icon: PhotovoltaicsIcon,
        iconName: 'PhotovoltaicsIcon',
        label: 'Photovoltaics',
    },
    {
        accent: 'electricity',
        icon: ElectricityIcon,
        iconName: 'ElectricityIcon',
        label: 'Electricity',
    },
    {
        accent: 'heat',
        icon: HeatIcon,
        iconName: 'HeatIcon',
        label: 'Heat',
    },
    {
        accent: 'photovoltaics',
        icon: PhotovoltaicsIcon,
        iconName: 'PhotovoltaicsIcon',
        label: 'Photovoltaics',
        selectLabel: 'Photovoltaics with Sensor',
        sensors: ['Mue.0531.pP_00011911'],
    },
    {
        accent: 'electricity',
        icon: ElectricityIcon,
        iconName: 'ElectricityIcon',
        label: 'Electricity',
        selectLabel: 'Electricity with Sensor',
        sensors: ['Mue.0531.pS_00001511'],
    },
    {
        accent: 'heat',
        icon: HeatIcon,
        iconName: 'HeatIcon',
        label: 'Heat',
        selectLabel: 'Heat with Sensor',
        sensors: ['Mue.0531.f_00001517'],
    },
    {
        accent: 'photovoltaics',
        icon: PhotovoltaicsIcon,
        iconName: 'PhotovoltaicsIcon',
        label: 'Photovoltaics',
        selectLabel: 'Photovoltaics with multiple Sensors',
        sensors: ['Mue.0531.pP_00011911', 'Mue.0531.pP_00011912', 'Mue.0531.pP_00011913'],
    },
    {
        accent: 'electricity',
        icon: ElectricityIcon,
        iconName: 'ElectricityIcon',
        label: 'Electricity',
        selectLabel: 'Electricity with multiple Sensors',
        sensors: ['Mue.0531.pS_00001511', 'Mue.0531.pS_00001512', 'Mue.0531.pS_00001513'],
    },
    {
        accent: 'heat',
        icon: HeatIcon,
        iconName: 'HeatIcon',
        label: 'Heat',
        selectLabel: 'Heat with multiple Sensors',
        sensors: ['Mue.0531.f_00001517', 'Mue.0531.f_00001518', 'Mue.0531.f_00001519'],
    },
    {
        accent: 'photovoltaics',
        anomalies: { warningCount: 1 },
        icon: PhotovoltaicsIcon,
        iconName: 'PhotovoltaicsIcon',
        label: 'Photovoltaics',
        selectLabel: 'Photovoltaics with multiple Sensors (1 x warning)',
        sensors: ['Mue.0531.pP_00011911', 'Mue.0531.pP_00011912', 'Mue.0531.pP_00011913'],
    },
    {
        accent: 'photovoltaics',
        anomalies: { errorCount: 2 },
        icon: PhotovoltaicsIcon,
        iconName: 'PhotovoltaicsIcon',
        label: 'Photovoltaics',
        selectLabel: 'Photovoltaics with multiple Sensors (2 x error)',
        sensors: ['Mue.0531.pP_00011911', 'Mue.0531.pP_00011912', 'Mue.0531.pP_00011913'],
    },
    {
        accent: 'photovoltaics',
        anomalies: { errorCount: 2, warningCount: 1 },
        icon: PhotovoltaicsIcon,
        iconName: 'PhotovoltaicsIcon',
        label: 'Photovoltaics',
        selectLabel: 'Photovoltaics with multiple Sensors (1 x warning + 2 x error)',
        sensors: ['Mue.0531.pP_00011911', 'Mue.0531.pP_00011912', 'Mue.0531.pP_00011913'],
    },
    {
        accent: 'photovoltaics',
        icon: PhotovoltaicsIcon,
        iconName: 'PhotovoltaicsIcon',
        label: 'Photovoltaics',
        selectLabel: 'Photovoltaics with anomaly link',
        sensors: ['Mue.0531.pP_00011911', 'Mue.0531.pP_00011912', 'Mue.0531.pP_00011913'],
        to: '/systems/GN1/detail?anomaly=pv',
    },
] satisfies Array<{
    accent: SystemDashboardSectionLabelAccent;
    anomalies?: SystemDashboardSectionLabelAnomalies;
    icon: typeof PhotovoltaicsIcon;
    iconName: string;
    label: string;
    selectLabel?: string;
    sensors?: readonly string[];
    to?: string;
}>;

function createSystemDashboardSectionLabelCode({
    accent,
    anomalies,
    iconName,
    label,
    sensors,
    to,
}: typeof SYSTEM_DASHBOARD_SECTION_LABEL_EXAMPLES[number]): string {
    const anomaliesProperty = anomalies
        ? `\n    anomalies={{${[
            anomalies.warningCount === undefined ? null : ` warningCount: ${anomalies.warningCount}`,
            anomalies.errorCount === undefined ? null : ` errorCount: ${anomalies.errorCount}`,
        ].filter(Boolean).join(',')} }}`
        : '';
    const sensorsProperty = sensors
        ? `\n    sensors={[${sensors.map((sensor) => `'${sensor}'`).join(', ')}]}`
        : '';
    const sensorChangeProperty = sensors && sensors.length > 1
        ? '\n    onSensorChange={(sensor) => window.alert(sensor)}'
        : '';
    const linkProperty = to ? `\n    to="${to}"` : '';

    return `import { ${iconName} } from '@/components/icon/SupplyIcon';
import SystemDashboardSectionLabel from '@/components/tabs/overview/system/SystemDashboardSectionLabel';

<SystemDashboardSectionLabel
    accent="${accent}"
    icon={${iconName}}
    label="${label}"${anomaliesProperty}${sensorsProperty}${sensorChangeProperty}${linkProperty}
/>`;
}

function showSelectedSensorAlert(sensor: string) {
    window.alert(sensor);
}

const SYSTEM_DASHBOARD_TILE_EXAMPLES = [
    {
        code: `import SystemDashboardTile from '@/components/tabs/overview/system/SystemDashboardTile';

<SystemDashboardTile
    severity="ok"
    title="Active / Generation"
    statusLabel="OK"
    value="12.5 MWh"
    detail="Mue.0531.pP_00011911"
    detailIsSensor
/>`,
        label: 'KPI tile',
        props: {
            detail: 'Mue.0531.pP_00011911',
            detailIsSensor: true,
            severity: 'ok',
            statusLabel: 'OK',
            title: 'Active / Generation',
            value: '12.5 MWh',
        },
    },
    {
        code: `import SystemDashboardTile from '@/components/tabs/overview/system/SystemDashboardTile';

<SystemDashboardTile
    severity="warning"
    title="Base Load"
    statusLabel="Warning"
    value="0.08 kW"
/>`,
        label: 'Warning tile',
        props: {
            severity: 'warning',
            statusLabel: 'Warning',
            title: 'Base Load',
            value: '0.08 kW',
        },
    },
    {
        code: `import { CircleDollarSign } from 'lucide-react';
import SystemDashboardTile from '@/components/tabs/overview/system/SystemDashboardTile';

<SystemDashboardTile
    actionIcon={CircleDollarSign}
    severity="warning"
    title="Price Traffic Light"
    statusLabel="Normal"
    value="82.5 €/MWh"
    onClick={() => window.alert('Open price traffic light')}
/>`,
        label: 'Interactive action tile',
        onClick: () => window.alert('Open price traffic light'),
        props: {
            actionIcon: CircleDollarSign,
            severity: 'warning',
            statusLabel: 'Normal',
            title: 'Price Traffic Light',
            value: '82.5 €/MWh',
        },
    },
    {
        code: `import SystemDashboardTile from '@/components/tabs/overview/system/SystemDashboardTile';

<SystemDashboardTile
    severity="ok"
    title="Modal Information"
    statusLabel="OK"
    value="Available"
    modal={{
        title: 'Modal Information',
        content: 'Additional information is displayed in this modal.',
    }}
/>`,
        label: 'Modal information',
        props: {
            modal: {
                title: 'Modal Information',
                content: 'Additional information is displayed in this modal.',
            },
            severity: 'ok',
            statusLabel: 'OK',
            title: 'Modal Information',
            value: 'Available',
        },
    },
] satisfies Array<{
    code: string;
    label: string;
    onClick?: () => void;
    props: SystemDashboardTileData;
}>;

function ComponentsLayoutsTab({ theme }: ComponentsLayoutsTabProps) {
    const [selectedExampleIndex, setSelectedExampleIndex] = useState(0);
    const selectedExample = SYSTEM_DASHBOARD_SECTION_LABEL_EXAMPLES[selectedExampleIndex]!;
    const [selectedTileExampleIndex, setSelectedTileExampleIndex] = useState(0);
    const selectedTileExample = SYSTEM_DASHBOARD_TILE_EXAMPLES[selectedTileExampleIndex]!;

    return (
        <div className="layouts-tab">
            <LayoutSectionHeader sectionKey="components" />

            <section className="layouts-section">
                <h3 className="text-xl font-semibold">SystemDashboardSectionLabel</h3>
                <p className="text-base-content/70">
                    Displays a centered icon and label for a supply section in the system dashboard.
                </p>

                <div className="inline-flex flex-wrap items-center gap-3">
                    <label className="text-sm font-semibold" htmlFor="system-dashboard-section-label-example">
                        Section label example
                    </label>
                    <select
                        id="system-dashboard-section-label-example"
                        className="select select-bordered w-auto"
                        value={selectedExampleIndex}
                        onChange={(event) => setSelectedExampleIndex(Number(event.target.value))}
                    >
                        {SYSTEM_DASHBOARD_SECTION_LABEL_EXAMPLES.map((example, index) => (
                            <option key={`${example.accent}-${example.selectLabel ?? example.label}`} value={index}>
                                {example.selectLabel ?? example.label}
                            </option>
                        ))}
                    </select>
                </div>

                <h4 className="tracking-wide">Code example</h4>
                <div className="layouts-code-tabs">
                    <div className="tabs tabs-lift">
                        <button type="button" className="tab tab-active">
                            JSX
                        </button>
                    </div>
                    <CodeBlock
                        className="layouts-code--tabbed"
                        code={createSystemDashboardSectionLabelCode(selectedExample)}
                        roundedTop={false}
                        theme={theme}
                    />
                </div>

                <h4 className="tracking-wide">Rendered preview</h4>
                <section className="dashboard-panel layouts-demo-panel items-start justify-start p-6">
                    <div className="h-[165px] w-[270px] max-w-full">
                        <SystemDashboardSectionLabel
                            key={`${selectedExample.accent}-${selectedExample.selectLabel ?? selectedExample.label}`}
                            accent={selectedExample.accent}
                            anomalies={selectedExample.anomalies}
                            icon={selectedExample.icon}
                            label={selectedExample.label}
                            sensors={selectedExample.sensors}
                            to={selectedExample.to}
                            onSensorChange={selectedExample.sensors && selectedExample.sensors.length > 1
                                ? showSelectedSensorAlert
                                : undefined}
                        />
                    </div>
                </section>
            </section>

            <section className="layouts-section">
                <h3 className="text-xl font-semibold">SystemDashboardTile</h3>
                <p className="text-base-content/70">
                    Displays a status KPI or action in the system dashboard.
                </p>

                <div className="inline-flex flex-wrap items-center gap-3">
                    <label className="text-sm font-semibold" htmlFor="system-dashboard-tile-example">
                        Tile example
                    </label>
                    <select
                        id="system-dashboard-tile-example"
                        className="select select-bordered w-auto"
                        value={selectedTileExampleIndex}
                        onChange={(event) => setSelectedTileExampleIndex(Number(event.target.value))}
                    >
                        {SYSTEM_DASHBOARD_TILE_EXAMPLES.map((example, index) => (
                            <option key={example.label} value={index}>
                                {example.label}
                            </option>
                        ))}
                    </select>
                </div>

                <h4 className="tracking-wide">Code example</h4>
                <div className="layouts-code-tabs">
                    <div className="tabs tabs-lift">
                        <button type="button" className="tab tab-active">
                            JSX
                        </button>
                    </div>
                    <CodeBlock
                        className="layouts-code--tabbed"
                        code={selectedTileExample.code}
                        roundedTop={false}
                        theme={theme}
                    />
                </div>

                <h4 className="tracking-wide">Rendered preview</h4>
                <section className="dashboard-panel layouts-demo-panel items-start justify-start p-6">
                    <div className="h-[165px] w-[540px] max-w-full">
                        <SystemDashboardTile
                            {...selectedTileExample.props}
                            onClick={selectedTileExample.onClick}
                        />
                    </div>
                </section>
            </section>
        </div>
    );
}

export default ComponentsLayoutsTab;
